/**
 * quoteLifecycle.js — Quote lifecycle state machine and transition logic.
 *
 * Implements rules:
 *   Q-1: No "Draft" — Active once ≥1 line item; auto-expire after validity window
 *   Q-3 [IDI-133 2026-09-01, RETIRED]: Mark Sent action is deprecated.
 *        IDI-132 (D-01): Mark Unsent is replaced by Reopen and Edit
 *        (canReopen), the only way to undo a send or a signature.
 *   Q-4 [IDI-133 2026-09-01, SUPERSEDED]: Convert to Job is gated by STATE,
 *        not by signature. Convertible statuses: active, sent, won. Expired
 *        quotes reactivate first (canReactivate → active → convert).
 *        Signature-required behaviour retired. See canConvertToJob below.
 *
 * States (per §E tone canon):
 *   active   → positive tone (red) — quote has ≥1 line item
 *   sent     → dark tone — externally delivered
 *   expired  → warning tone — past validity window
 *   won      → success tone (green), label "Accepted" (IDI-370)
 *   converted → positive tone — converted to job
 *   lost     → neutral tone — manually marked lost
 *
 * NOTE: "Draft" does NOT exist in the UI. The backend may use "draft" internally
 * for quotes with 0 line items, but the frontend never displays it.
 */

import { convertedJobIdOf } from '../utils/quoteRouting';

// ─── State Definitions ───────────────────────────────────────────────────────

export const QUOTE_STATES = {
  ACTIVE: 'active',
  SENT: 'sent',
  EXPIRED: 'expired',
  WON: 'won',
  CONVERTED: 'converted',
  LOST: 'lost',
};

// ─── Valid Transitions ───────────────────────────────────────────────────────

const TRANSITIONS = {
  active: ['sent', 'expired', 'converted', 'lost'],
  sent: ['active', 'expired', 'converted', 'lost'],  // active = Reopen and Edit (IDI-132)
  expired: ['active'],                                 // Can reactivate
  converted: [],                                       // Terminal state
  lost: ['active'],                                    // Can reactivate
};

/**
 * Check if a transition is valid.
 * @param {string} from - Current state
 * @param {string} to - Target state
 * @returns {boolean}
 */
export function canTransition(from, to) {
  const validTargets = TRANSITIONS[from];
  if (!validTargets) return false;
  return validTargets.includes(to);
}

// ─── Q-1: Status Resolution ─────────────────────────────────────────────────

/**
 * Q-1: Resolve the display status for a quote.
 * - Backend "draft" with ≥1 line item → "active" (no Draft in UI)
 * - Backend "draft" with 0 line items → still "active" (empty state shown differently)
 *
 * @param {string} backendStatus - Status from the backend
 * @param {number} lineItemCount - Number of line items
 * @returns {string} Display status for the UI
 */
export function resolveDisplayStatus(backendStatus, lineItemCount) {
  // Q-1: No "Draft" in UI — always show as Active
  if (backendStatus === 'draft') return QUOTE_STATES.ACTIVE;

  // Normalize backend status
  const normalized = String(backendStatus).toLowerCase().replace(/[_\s]+/g, '_');

  // Map known backend statuses
  switch (normalized) {
    case 'active':
    case 'unsent':
      return QUOTE_STATES.ACTIVE;
    case 'sent':
      return QUOTE_STATES.SENT;
    case 'expired':
      return QUOTE_STATES.EXPIRED;
    case 'converted':
      return QUOTE_STATES.CONVERTED;
    case 'won':
    case 'accepted':
      // IDI-370 (Option A ruling, 2026-09-30) supersedes the §19 fold: an
      // accepted quote (e-signed, Record Decision, or Convert) renders as its
      // own green "Accepted" pill, never "Active". Actions and the convert
      // gate are unchanged — WON is treated like ACTIVE there.
      return QUOTE_STATES.WON;
    case 'lost':
      return QUOTE_STATES.LOST;
    default:
      return QUOTE_STATES.ACTIVE;
  }
}

// ─── Q-1: Auto-Expire Logic ─────────────────────────────────────────────────

/**
 * Q-1: Check if a quote should be auto-expired based on validity window.
 *
 * @param {string} createdAt - ISO timestamp of quote creation
 * @param {number} validityDays - Validity window in days (from Settings)
 * @param {string} currentStatus - Current quote status
 * @returns {{isExpired: boolean, daysRemaining: number, expiresAt: string}}
 */
export function checkExpiry(createdAt, validityDays = 30, currentStatus = 'active') {
  if (!createdAt || !validityDays) {
    return { isExpired: false, daysRemaining: Infinity, expiresAt: null };
  }

  // Only active/sent quotes can expire
  if (currentStatus !== 'active' && currentStatus !== 'sent') {
    return { isExpired: false, daysRemaining: Infinity, expiresAt: null };
  }

  const created = new Date(createdAt);
  const expiresAt = new Date(created.getTime() + validityDays * 24 * 60 * 60 * 1000);
  const now = new Date();
  const daysRemaining = Math.ceil((expiresAt - now) / (24 * 60 * 60 * 1000));

  return {
    isExpired: daysRemaining <= 0,
    daysRemaining: Math.max(0, daysRemaining),
    expiresAt: expiresAt.toISOString(),
  };
}

// ─── Q-3: Mark Sent / Unsent Logic ──────────────────────────────────────────

/**
 * getAvailableActions — determine available actions from a quote's current
 * state.
 *
 * IDI-133 completion (2026-09-01, Portica #45 §4): `hasSignature` parameter
 * DELETED. It was retained temporarily as an ESLint-disabled unused param
 * when IDI-133 shipped (#310), preserving the function signature for any
 * legacy caller. Two deploys later, no production caller passes it. The
 * regression-control test that asserted hasSignature is ignored
 * (`quoteLifecycle.test.js` "hasSignature no longer consulted") now passes
 * hasSignature to a spread-and-discard shape that documents its
 * ex-parameter status without depending on the function signature accepting
 * it.
 *
 * IDI-132 (D-01): Mark Unsent is gone; Reopen and Edit (canReopen) is the
 * only way to undo a send or a signature. Sent and won-without-a-job quotes
 * are locked (canEdit false). The optional second argument is the quote
 * record ({ signatureStatus, convertedJobId }); anything that is not an
 * object is ignored. A signed quote (signatureStatus acknowledged — it may
 * still read "sent" before IDI-370's backfill) is treated as won; a
 * converted one offers nothing but View Job.
 *
 * @param {string} status - Current quote status (active/sent/won/expired/lost/converted)
 * @param {object=} quote - Optional quote record
 * @returns {object} Available actions
 */
export function getAvailableActions(status, quote) {
  const q = quote && typeof quote === 'object' ? quote : null;
  const signed = !!q && (q.signatureStatus || q.signature_status) === 'acknowledged';
  const converted = !!q && !!convertedJobIdOf(q);
  if (converted) status = QUOTE_STATES.CONVERTED;
  else if (signed && (status === QUOTE_STATES.SENT || status === QUOTE_STATES.ACTIVE || status === 'draft')) status = QUOTE_STATES.WON;
  const actions = {
    // IDI-133 (2026-09-01): "canMarkSent" retired; "canConvert" renamed to
    // "canConvertToJob" with state-based gate (see header Q-3/Q-4 notes).
    canEdit: false,
    canReopen: false,
    canResend: false,
    canConvertToJob: false,
    canMarkLost: false,
    canDownload: false,
    canReactivate: false,
  };

  switch (status) {
    // IDI-313 (2026-09-14): `draft` gets the SAME actions as ACTIVE. The
    // earlier (2026-09-10) all-false `draft` case caused a regression once
    // activate-on-first-save landed: QuoteBuilderPage's More menu calls
    // getAvailableActions(quoteMeta.status) and quoteMeta.status is now
    // seeded to 'draft' on the first save (from the create response) — so a
    // freshly-saved quote WITH line items routed through the all-false case
    // and "Convert to Job"/"Mark Unsent" vanished until the next autosave
    // flipped it to 'active'. This is inconsistent with the app-wide
    // invariant: resolveDisplayStatus('draft') === 'active' and Q-1 ("no
    // Draft in the UI"). A stored/transient draft is treated as active
    // everywhere it's shown, so its actions must match. The 0-line-item
    // concern the old comment raised doesn't apply at the only call site
    // (the menu renders for a saved quote that already has content).
    case 'draft':
    case QUOTE_STATES.ACTIVE:
      // IDI-133 (2026-09-01): "canMarkSent" retired. "canConvertToJob"
      // replaces "canConvert"; gate is state-only, not hasSignature.
      actions.canEdit = true;
      actions.canConvertToJob = true;
      actions.canMarkLost = true;
      actions.canDownload = true;
      break;

    case QUOTE_STATES.WON:
      // IDI-132 / IDI-533 (D-01): signed, not converted — locked. More:
      // Reopen and Edit, Download PDF, Archive. No Resend (IDI-549), no
      // Mark lost.
      actions.canReopen = true;
      actions.canConvertToJob = true;
      actions.canDownload = true;
      break;

    case QUOTE_STATES.SENT:
      // IDI-132 (D-01): sent — locked. More: Reopen and Edit, Download PDF,
      // Resend, Mark lost, Archive. Convert to Job is the primary action.
      actions.canReopen = true;
      actions.canResend = true;
      actions.canConvertToJob = true;
      actions.canMarkLost = true;
      actions.canDownload = true;
      break;

    case QUOTE_STATES.EXPIRED:
      actions.canReactivate = true;
      actions.canDownload = true;
      break;

    case QUOTE_STATES.LOST:
      actions.canReactivate = true;
      actions.canDownload = true;
      break;

    case QUOTE_STATES.CONVERTED:
      // Terminal — view only
      actions.canDownload = true;
      break;

    default:
      break;
  }

  return actions;
}

// ─── IDI-132: Locked landing ────────────────────────────────────────────────

/**
 * getQuoteLockState — how a quote opened in the builder is locked (D-01).
 *   'converted' — has a job (any status): View Job only.
 *   'signed'    — signatureStatus acknowledged (it may still read "sent"
 *                 before IDI-370's backfill), or won/accepted without a job.
 *   'sent'      — sent, not signed.
 *   null        — editable (active, draft, lost, expired, archived).
 * Locked: lines, prices, selections, customer, terms. Internal notes, tags
 * and files stay editable. Only Reopen and Edit unlocks sent/signed.
 */
export function getQuoteLockState(quote) {
  if (!quote || typeof quote !== 'object') return null;
  if (convertedJobIdOf(quote)) return 'converted';
  const sig = quote.signatureStatus || quote.signature_status;
  const status = String(quote.status || '').toLowerCase();
  if (sig === 'acknowledged' || status === 'won' || status === 'accepted') return 'signed';
  if (status === 'sent') return 'sent';
  return null;
}

/** Banner / section title for a lock state. */
export function quoteLockLabel(lockState) {
  if (lockState === 'sent') return 'Sent — locked';
  if (lockState === 'signed') return 'Signed — locked';
  if (lockState === 'converted') return 'Converted — locked';
  return '';
}

// ─── Reopen and Edit confirm (IDI-132; shared by IDI-568) ────────────────────────

/**
 * reopenConfirmOptions — the useConfirm() options for Reopen and Edit. One
 * copy for the builder and Record Client Decision (IDI-568, D4), so both say
 * the same thing.
 * @param {boolean} signed - the customer has signed (lock state 'signed')
 */
export function reopenConfirmOptions(signed) {
  return {
    title: 'Reopen and edit this quote?',
    message: signed
      ? 'The customer has signed this quote. Reopening voids their signature: it can no longer be converted on that signature, and you will need to re-send it or record the decision again. The signed copy stays in Acknowledgments.'
      : 'The signing link the customer was sent will stop working. The quote returns to Active so you can edit it and send it again.',
    confirmLabel: 'Reopen and Edit',
    tone: signed ? 'danger' : 'primary',
  };
}

// ─── Convert Gate (IDI-133 2026-09-01 rewrite) ──────────────────────────────

/**
 * checkConvertEligibility — check if conversion to job is allowed.
 *
 * IDI-133 (2026-09-01, supersedes Q-4): gated by STATE, not by signature.
 * Convertible statuses: active, sent, won. Expired quotes must reactivate
 * first (see canReactivate). requiresConfirmation is always false — no
 * confirm-accepted modal since signature is no longer part of the gate.
 *
 * Return shape retained for backward-compat with any callers using
 * requiresConfirmation, but the field is now always false.
 *
 * @param {object} quote - Quote data
 * @returns {{allowed: boolean, requiresConfirmation: boolean, reason: string|null}}
 */
export function checkConvertEligibility(quote) {
  if (!quote) {
    return { allowed: false, requiresConfirmation: false, reason: 'No quote data' };
  }

  const status = resolveDisplayStatus(quote.status, quote.lineItems?.length || 0);

  // IDI-133: convertible states are active, sent, won.
  const CONVERTIBLE = new Set([QUOTE_STATES.ACTIVE, QUOTE_STATES.SENT, QUOTE_STATES.WON]);
  if (!CONVERTIBLE.has(status)) {
    const reason = status === QUOTE_STATES.EXPIRED
      ? 'Expired quotes must be reactivated before converting.'
      : `Cannot convert from ${status} state`;
    return { allowed: false, requiresConfirmation: false, reason };
  }

  return { allowed: true, requiresConfirmation: false, reason: null };
}

// ─── Validity Helper Text ────────────────────────────────────────────────────

/**
 * Generate the "expires in N days" helper text for the builder.
 *
 * @param {number} daysRemaining - Days until expiry
 * @returns {string} Human-readable expiry text
 */
export function expiryHelperText(daysRemaining) {
  if (daysRemaining === Infinity || daysRemaining == null) return '';
  if (daysRemaining <= 0) return 'Expired';
  if (daysRemaining === 1) return 'Expires tomorrow';
  if (daysRemaining <= 7) return `Expires in ${daysRemaining} days`;
  return `Expires in ${daysRemaining} days`;
}
